import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Capa, CapaECTypes, CapaPic } from '../../audit.model';
import { Observable, ReplaySubject, Subject, timer } from 'rxjs';
import { CapaService } from '../../capa.service';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { MatSelect } from '@angular/material';
import { Employee } from '../../../shared/shared.model';
import { EmployeesService } from '../../../shared/employees.service';
import { SidService } from '../../sid.service';
import { Sid } from '../../sid.model';

@Component({
  selector: 'iaud-capa-edit-form',
  templateUrl: './capa-edit-form.component.html',
  styleUrls: ['./capa-edit-form.component.scss'],
})
export class CapaEditFormComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('pics') pics: MatSelect;

  today: Date = moment().toDate();
  capaForm: FormGroup;
  pics$: Observable<Array<CapaPic>>;
  capaTypes = CapaECTypes;
  sid$:Observable<Sid>;
  sid : Sid;
  canUpdate  : boolean;
  isDraft :boolean;

  @Input()
  capa: Capa;
  @Input()
  isNew: boolean = false;
  @Output()
  isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()
  maxDate: Date;  

  protected _onDestroy = new Subject<void>();
  searching: boolean = false;
  public filteredPics: ReplaySubject<Array<Employee>> = new ReplaySubject<Array<Employee>>(1);
  public previousPics: Array<Employee>;

  constructor(
    private formBuilder: FormBuilder,
    private capaService: CapaService,
    private empSvc: EmployeesService,
    private sidService: SidService
  ) { }

  ngOnInit(): void {
    this.capaForm = this.generateForm();

    if (!this.isNew) {
      this.isValidChange.emit(this.capaForm.valid);
    }

    this.pics$ = this.capaService.getPics();    

    if (this.capaForm.get('pics').value) {
      this.previousPics = this.capaForm.get('pics').value;
      this.filteredPics.next(this.capaForm.get('pics').value);
    }
    
    if(this.capa.sidId!=null){
      this.sidService.get(this.capa.sidId)
        .subscribe(x => {
          this.sid = x;   
          this.maxDate = this.sid.dueDate;        
        }); 
    }     

    this.capaForm.statusChanges
      .subscribe(() => {
        this.isValidChange.emit(this.capaForm.valid);
      });

    this.capaForm.valueChanges
      .pipe(
        distinctUntilChanged()
      )
      .subscribe(values => {
        this.capa.capaTitle = values.title;
        this.capa.capaEcType = values.capaEcType;
        this.capa.evidenceDueDate = values.evidenceDueDate;
        this.capa.capaPics = values.pics;
        this.capa.description = values.description;
        this.capa.allMustSubmit = values.submission === '1';
        this.capa.canUpdate = values.canUpdate;
      });

    this.capaForm.get('picsFilter').valueChanges
      .pipe(
        filter(search => !!search),
        tap(() => this.searching = true),
        takeUntil(this._onDestroy),
        debounceTime(200),
        map(search => {
          return this.empSvc.list(search).pipe(
            map(userList => {
              if (this.previousPics) {
                this.previousPics.forEach(x => {
                  if (!userList.find(emp => emp.userId === x.userId)) {
                    userList.push(x);
                  }
                });
              }

              return userList;
            })
          );
        })
      )
      .subscribe(filteredEmps => {
        this.searching = false;
        filteredEmps.subscribe(x => {
          return this.filteredPics.next(x);
        });
      },
        _error => {
          this.searching = false;
        });

  }
  generateForm(): FormGroup {
    const titleField = this.isNew ? [this.capa.capaTitle, Validators.required, this.isCapaTitleAvailableValidator.bind(this)]
      : [this.capa.capaTitle, Validators.required];

    this.canUpdate = this.capa.canUpdate;      
    this.isDraft = false;    

    if(this.capa.status.toLowerCase().trim() == "draft"){
      this.isDraft = true;
    } 
      
    return this.formBuilder.group({
      title: titleField,
      evidenceDueDate: [this.capa.evidenceDueDate, Validators.required],
      capaEcType: [this.capa.capaEcType, Validators.required],
      pics: [this.capa.capaPics, Validators.required],
      picsFilter: null,
      description: [this.capa.description, Validators.required],
      submission: [this.capa.allMustSubmit ? '1' : '2'],
    });
  }

  ngAfterViewInit(): void {
    this.pics.compareWith = (a: Employee, b: Employee) => {
      return a && b && a.userId === b.userId;
    };
    this.pics.valueChange
      .subscribe(values => {
        let restoreSelectedValues = true;

        if (values && values.length && this.previousPics && Array.isArray(this.previousPics)) {
          const optionValues = this.pics.options.map(option => option.value);
          this.previousPics.forEach(previousValue => {
            if (values.indexOf(previousValue) === -1 && optionValues.indexOf(previousValue) === 1) {
              values.push(previousValue);
              restoreSelectedValues = true;
            }
          });
        }
        if (restoreSelectedValues) {
          this.pics._onChange(values);
        }

        this.previousPics = values;
      });
  }
  isCapaTitleAvailableValidator(control: AbstractControl): Observable<ValidationErrors> {
    return timer(500)
      .pipe(
        switchMap(() => {
          if (this.capa.findingDataId) {
            return this.capaService.checkCapaExistence(this.capa.findingDataId, control.value)
              .pipe(
                distinctUntilChanged(),
                map((exists: boolean) => exists ? { exists: true } : null)
              );
          } else {
            return this.capaService.checkCapaSidExistence(this.capa.sidId, control.value)
              .pipe(
                distinctUntilChanged(),
                map((exists: boolean) => exists ? { exists: true } : null)
              );
          }
        })
      );
  }
  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
